import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/client';

const DEBOUNCE_MS = 600;

function fmtLessonWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function CommentIcon({ kind, text }) {
  if (!text) return null;
  const label = kind === 'teacher' ? 'Комментарий преподавателя' : 'Комментарий ученика';
  return (
    <span
      className={`lesson-journal-comment-icon lesson-journal-comment-icon--${kind}`}
      tabIndex={0}
      aria-label={label}
    >
      <span className="lesson-journal-comment-glyph" aria-hidden>
        {kind === 'teacher' ? '💬' : '🗒'}
      </span>
      <span className="lesson-journal-comment-tip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function ReportGroupLine({ group, onSelectTaskSlug }) {
  return (
    <div className="lesson-journal-report-line">
      <span className="lesson-journal-exam-label">{group.label}:</span>
      <span className="lesson-journal-report-items">
        {(group.items || []).map((item) => {
          const clickable = Boolean(onSelectTaskSlug && item.task_slug);
          const inner = (
            <>
              <span className="lesson-journal-task-num">{item.display_number}</span>
              <span className="lesson-journal-mark">{item.mark || '—'}</span>
              {item.has_self_comment ? (
                <CommentIcon kind="student" text={item.self_comment} />
              ) : null}
              {item.has_teacher_comment ? (
                <CommentIcon kind="teacher" text={item.teacher_comment} />
              ) : null}
            </>
          );
          return (
            <span key={item.attempt_id || `${item.task_id}-${item.display_number}`} className="lesson-journal-task-chip">
              {clickable ? (
                <button
                  type="button"
                  className="link-like lesson-journal-task-btn"
                  title={item.task_title || item.task_slug}
                  onClick={() => onSelectTaskSlug(item.task_slug)}
                >
                  {inner}
                </button>
              ) : (
                <span className="lesson-journal-task-static" title={item.task_title || ''}>
                  {inner}
                </span>
              )}
            </span>
          );
        })}
      </span>
    </div>
  );
}

/**
 * Журнал занятий (LessonSession): даты, компактный отчёт, заметки.
 *
 * mode:
 * - 'teacher' — GET /teacher/students/:id/lesson-sessions/, без правки student_notes
 * - 'student' — GET /tasks/lesson-sessions/?journal=1, можно править student_notes
 */
export default function LessonJournal({
  mode = 'student',
  studentId = null,
  onSelectTaskSlug = null,
}) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notesDrafts, setNotesDrafts] = useState({});
  const [notesSaved, setNotesSaved] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const debounceRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = mode === 'teacher'
        ? await api.get(`/teacher/students/${studentId}/lesson-sessions/`)
        : await api.get('/tasks/lesson-sessions/', { params: { journal: 1 } });
      const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setSessions(list);
      if (mode === 'student') {
        const drafts = {};
        for (const s of list) {
          drafts[s.id] = s.student_notes || '';
        }
        setNotesDrafts(drafts);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось загрузить журнал занятий.';
      setError(typeof msg === 'string' ? msg : 'Не удалось загрузить журнал занятий.');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [mode, studentId]);

  useEffect(() => {
    if (mode === 'teacher' && !studentId) return;
    void load();
  }, [load, mode, studentId]);

  useEffect(() => () => {
    Object.values(debounceRefs.current).forEach((t) => clearTimeout(t));
  }, []);

  const saveStudentNotes = useCallback(async (sessionId, text) => {
    setSavingNotes((prev) => ({ ...prev, [sessionId]: true }));
    try {
      await api.patch(`/tasks/lesson-sessions/${sessionId}/`, { student_notes: text });
      setSessions((prev) => prev.map((s) => (
        s.id === sessionId ? { ...s, student_notes: text } : s
      )));
      setNotesSaved((prev) => ({ ...prev, [sessionId]: true }));
      setTimeout(() => {
        setNotesSaved((prev) => ({ ...prev, [sessionId]: false }));
      }, 1500);
    } catch {
      setError('Не удалось сохранить комментарий к занятию.');
    } finally {
      setSavingNotes((prev) => ({ ...prev, [sessionId]: false }));
    }
  }, []);

  const onStudentNotesChange = (sessionId, text) => {
    setNotesDrafts((prev) => ({ ...prev, [sessionId]: text }));
    if (debounceRefs.current[sessionId]) {
      clearTimeout(debounceRefs.current[sessionId]);
    }
    debounceRefs.current[sessionId] = setTimeout(() => {
      void saveStudentNotes(sessionId, text);
    }, DEBOUNCE_MS);
  };

  if (loading) {
    return <p className="text-secondary">Загрузка журнала занятий…</p>;
  }

  return (
    <div className="lesson-journal">
      {error ? <div className="alert alert-error">{error}</div> : null}
      {sessions.length === 0 ? (
        <p className="text-secondary">Пока нет занятий в журнале.</p>
      ) : (
        <ul className="lesson-journal-list">
          {sessions.map((session) => {
            const groups = Array.isArray(session.report_groups) ? session.report_groups : [];
            const ended = Boolean(session.ended_at);
            return (
              <li key={session.id} className={`lesson-journal-card ${ended ? 'is-ended' : 'is-active'}`}>
                <header className="lesson-journal-card-head">
                  <div>
                    <strong>{fmtLessonWhen(session.started_at)}</strong>
                    {ended ? (
                      <span className="text-secondary lesson-journal-end">
                        {' '}— {fmtLessonWhen(session.ended_at)}
                      </span>
                    ) : (
                      <span className="badge session-badge lesson-journal-live">Идёт</span>
                    )}
                  </div>
                  {mode === 'student' && session.teacher_username ? (
                    <span className="text-secondary">с @{session.teacher_username}</span>
                  ) : null}
                </header>

                <div className="lesson-journal-report">
                  {groups.length === 0 ? (
                    <p className="text-secondary lesson-journal-empty-report">
                      Нет зафиксированных попыток по этому занятию.
                    </p>
                  ) : (
                    groups.map((group) => (
                      <ReportGroupLine
                        key={`${session.id}-${group.label}`}
                        group={group}
                        onSelectTaskSlug={onSelectTaskSlug}
                      />
                    ))
                  )}
                </div>

                <div className="lesson-journal-notes">
                  <div className="lesson-journal-note-block">
                    <div className="lesson-journal-note-label">Комментарий преподавателя</div>
                    {session.teacher_notes?.trim() ? (
                      <p className="lesson-journal-note-text">{session.teacher_notes}</p>
                    ) : (
                      <p className="text-secondary">Нет комментария.</p>
                    )}
                  </div>
                  <div className="lesson-journal-note-block">
                    <div className="lesson-journal-note-label">
                      Комментарий ученика
                      {mode === 'student' && savingNotes[session.id] ? (
                        <span className="text-secondary"> · сохранение…</span>
                      ) : null}
                      {mode === 'student' && notesSaved[session.id] ? (
                        <span className="text-secondary"> · сохранено</span>
                      ) : null}
                    </div>
                    {mode === 'student' ? (
                      <textarea
                        className="form-textarea lesson-journal-notes-input"
                        rows={3}
                        value={notesDrafts[session.id] ?? session.student_notes ?? ''}
                        onChange={(e) => onStudentNotesChange(session.id, e.target.value)}
                        placeholder="Что запомнить по этому занятию…"
                      />
                    ) : session.student_notes?.trim() ? (
                      <p className="lesson-journal-note-text">{session.student_notes}</p>
                    ) : (
                      <p className="text-secondary">Нет комментария.</p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <button type="button" className="btn btn-xs btn-outline" onClick={() => void load()} disabled={loading}>
        Обновить
      </button>
    </div>
  );
}
